import React, { useState } from "react";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    Grid,
    InputAdornment,
    Typography,
    TextField,
    Snackbar,
    Alert,
    Box,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorIcon from "@mui/icons-material/Error";
import SendIcon from "@mui/icons-material/Send";
import axios from "axios";
import image from '../images/Rv.jpg';

const LEAD_API_URL = "https://gmc.getmycollege.com/university/addExternalSourceLead";
const LEAD_TYPE = "TopPuColleges";

const Autopopup = ({ isOpen, onClose, leadFlag }) => {
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [nameError, setNameError] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const showSnackbar = (message, severity = 'success') =>
        setSnackbar({ open: true, message, severity });

    const handleCloseSnackbar = () =>
        setSnackbar(prev => ({ ...prev, open: false }));

    const validateName = (value) => {
        if (!value.trim()) { setNameError(''); return false; }
        if (!/^[A-Za-z\s]+$/.test(value)) {
            setNameError('Name should contain only letters'); return false;
        }
        if (value.trim().length < 3) {
            setNameError('Name must be at least 3 characters'); return false;
        }
        setNameError(''); return true;
    };

    const validatePhone = (value) => {
        if (!value) { setPhoneError(''); return false; }
        if (!/^\d{10}$/.test(value)) {
            setPhoneError('Enter a valid 10-digit phone number'); return false;
        }
        if (!/^[6-9]/.test(value)) {
            setPhoneError('Phone number should start with 6–9'); return false;
        }
        setPhoneError(''); return true;
    };

    const handleNameChange = (e) => { setName(e.target.value); if (e.target.value) validateName(e.target.value); };
    const handlePhoneChange = (e) => {
        const v = e.target.value.replace(/\D/g, '').slice(0, 10);
        setPhone(v); if (v) validatePhone(v);
    };

    const handleSubmit = async () => {
        const isNameValid = validateName(name);
        const isPhoneValid = validatePhone(phone);

        if (!name.trim() || !phone.trim()) {
            showSnackbar('Please fill in all required fields.', 'error'); return;
        }
        if (!isNameValid || !isPhoneValid) {
            showSnackbar('Please fix the errors before submitting.', 'error'); return;
        }

        const requestData = {
            name: name.trim(),
            phone: phone.trim(),
            leadType: LEAD_TYPE,
            leadFlag: leadFlag || 'Organic',
        };

        setLoading(true);
        try {
            const response = await axios.post(LEAD_API_URL, requestData, {
                headers: { 'Content-Type': 'application/json' },
            });

            if (response?.status >= 200 && response?.status < 300) {
                sessionStorage.setItem('isFormSubmitted', 'true');
                setSubmitted(true);
                showSnackbar(
                    'Thank you! Your application has been submitted. Our team will contact you soon.',
                    'success'
                );
                // Auto-close after 3 s so user can see success screen
                setTimeout(() => { onClose(); }, 3000);
            } else {
                throw new Error('Submission failed');
            }
        } catch (error) {
            console.error('Lead API error:', error);
            let msg = 'Something went wrong. Please try again later.';
            if (error.response) msg = error.response.data?.message || 'Server error. Please try again.';
            else if (error.request) msg = 'No response from server. Check your internet connection.';
            showSnackbar(msg, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && name && phone && !nameError && !phoneError && !loading) {
            handleSubmit();
        }
    };

    return (
        <>
            <Dialog
                open={isOpen}
                // ── Popup cannot be closed until lead is submitted ──────────
                onClose={() => { /* intentionally blocked */ }}
                disableEscapeKeyDown
                // ─────────────────────────────────────────────────────────────
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        overflow: 'hidden',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                    },
                }}
                maxWidth="md"
                sx={{ width: '100%', height: '100%', overflow: 'hidden' }}
            >
                <DialogContent sx={{ overflow: 'hidden', '&.MuiDialogContent-root': { padding: 0 } }}>
                    <Grid container sx={{ width: '100%', alignItems: 'stretch', overflow: 'hidden' }}>

                        {/* ── Left image panel ───────────────────────────────── */}
                        <Grid
                            item xs={12} md={5}
                            sx={{
                                display: { xs: 'none', md: 'block' },
                                position: 'relative',
                                backgroundImage: `url(${image})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                minHeight: '480px',
                            }}
                        >
                            <Box sx={{
                                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                background: 'linear-gradient(to bottom, rgba(26,35,126,0.9), rgba(26,35,126,0.75))',
                                zIndex: 1, display: 'flex', flexDirection: 'column',
                                justifyContent: 'center', p: 4,
                            }}>
                                <Typography variant="h3" sx={{ color: '#fff', fontWeight: 800, mb: 2 }}>
                                    Your Future Starts Here
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.8 }}>
                                    Get expert counseling to choose the right PU College for your ambitions.
                                </Typography>
                            </Box>
                        </Grid>

                        {/* ── Right form panel ───────────────────────────────── */}
                        <Grid
                            item xs={12} md={7}
                            sx={{
                                display: 'flex', flexDirection: 'column', justifyContent: 'center',
                                background: '#fff', p: { xs: 4, md: 6 },
                            }}
                        >
                            {submitted ? (
                                /* ── Success state ── */
                                <Box sx={{ textAlign: 'center', py: 6 }}>
                                    <CheckCircleOutlineIcon sx={{ fontSize: '80px', color: 'var(--primary-color)', mb: 3 }} />
                                    <Typography variant="h4" sx={{ fontWeight: 800, color: 'var(--primary-dark)', mb: 2 }}>
                                        Thank You, {name}!
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                        Our counselor will reach out to you on <strong>+91-{phone}</strong> shortly.
                                    </Typography>
                                </Box>
                            ) : (
                                /* ── Form state ── */
                                <Box sx={{ width: '100%' }}>
                                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                                        <Typography variant="h4" sx={{ fontWeight: 800, color: 'var(--primary-color)', mb: 1 }}>
                                            Apply for Admission
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            Fill in your details and we will get back to you shortly.
                                        </Typography>
                                    </Box>

                                    <Grid container spacing={3}>
                                        {/* Name */}
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                variant="outlined"
                                                label="Full Name *"
                                                placeholder="Enter your full name"
                                                value={name}
                                                onChange={handleNameChange}
                                                onKeyPress={handleKeyPress}
                                                error={!!nameError}
                                                helperText={nameError}
                                                disabled={loading}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <PersonIcon color="primary" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                            />
                                        </Grid>

                                        {/* Phone */}
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                variant="outlined"
                                                label="Phone Number *"
                                                placeholder="Enter 10-digit phone number"
                                                value={phone}
                                                onChange={handlePhoneChange}
                                                onKeyPress={handleKeyPress}
                                                error={!!phoneError}
                                                helperText={phoneError}
                                                disabled={loading}
                                                inputProps={{ maxLength: 10 }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <PhoneIcon color="primary" />
                                                            +91
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                            />
                                        </Grid>

                                        {/* Submit */}
                                        <Grid item xs={12}>
                                            <Button
                                                fullWidth
                                                onClick={handleSubmit}
                                                variant="contained"
                                                disabled={loading || !name || !phone || !!nameError || !!phoneError}
                                                startIcon={!loading && <SendIcon />}
                                                sx={{
                                                    mt: 1, py: 1.5, fontSize: '18px', fontWeight: 700,
                                                    textTransform: 'none', borderRadius: '30px',
                                                    background: 'var(--secondary-color)',
                                                    boxShadow: '0 8px 16px rgba(233,30,99,0.3)',
                                                    '&:hover': {
                                                        background: 'var(--secondary-light)',
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: '0 12px 20px rgba(233,30,99,0.4)',
                                                    },
                                                    '&:disabled': { background: '#cbd5e1', transform: 'none' },
                                                    transition: 'all 0.3s ease',
                                                }}
                                            >
                                                {loading
                                                    ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <CircularProgress size={22} color="inherit" />
                                                        <span>Processing...</span>
                                                    </Box>
                                                    : 'Apply Now — Get Free Counseling'
                                                }
                                            </Button>

                                            <Typography variant="caption" sx={{
                                                display: 'block', textAlign: 'center',
                                                color: 'text.secondary', mt: 2, lineHeight: 1.4,
                                            }}>
                                                By submitting, you agree to receive communication from Top PU Colleges.
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{ '& .MuiPaper-root': { borderRadius: '8px', boxShadow: '0 8px 32px rgba(0,0,0,0.25)' } }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%', fontWeight: 500, fontSize: '0.95rem', borderRadius: '8px' }}
                    iconMapping={{
                        success: <CheckCircleIcon fontSize="medium" />,
                        error: <ErrorIcon fontSize="medium" />,
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default Autopopup;